// Schemas for the CSV invite email feature. Invite recipients are imported
// email addresses (decoupled from tickets) that an organizer composes and sends
// a branded invite email to. The compose/send side reuses the email-campaign
// shapes; these schemas cover the recipient list, import, send, and resend.

const eventIdParams = {
  type: "object",
  properties: {
    eventId: { type: "string" },
  },
  required: ["eventId"],
};

const eventIdAndIdParams = {
  type: "object",
  properties: {
    eventId: { type: "string" },
    id: { type: "string" },
  },
  required: ["eventId", "id"],
};

// A single imported recipient row. firstName/lastName optional; email required.
const recipientRowSchema = {
  type: "object",
  additionalProperties: false,
  properties: {
    email: { type: "string", format: "email" },
    firstName: { type: ["string", "null"] },
    lastName: { type: ["string", "null"] },
  },
  required: ["email"],
};

// Import a CSV/XLSX file (base64), parsed server-side like the tickets import.
export const importInvitesSchema = {
  params: eventIdParams,
  body: {
    type: "object",
    additionalProperties: false,
    properties: {
      file: {
        type: "object",
        additionalProperties: false,
        properties: {
          name: { type: "string" },
          type: { type: "string" },
          data: { type: "string" },
        },
        required: ["data", "type"],
      },
    },
    required: ["file"],
  },
};

export const listInvitesSchema = {
  params: eventIdParams,
  query: {
    type: "object",
    properties: {
      sort: { type: "string" },
      filter: { type: "string" },
      page: { type: "integer", minimum: 1, default: 1 },
      limit: { type: "integer", minimum: 1, maximum: 100, default: 10 },
    },
  },
};

export const inviteStatsSchema = {
  params: eventIdParams,
};

export const upsertInviteRecipientSchema = {
  params: eventIdParams,
  body: recipientRowSchema,
};

export const updateInviteRecipientSchema = {
  params: eventIdAndIdParams,
  body: {
    type: "object",
    additionalProperties: false,
    properties: {
      email: { type: "string", format: "email" },
      firstName: { type: ["string", "null"] },
      lastName: { type: ["string", "null"] },
    },
  },
};

// Delete takes the recipient id in the body so the web hook can pass it at
// mutate time (mirrors the campaign delete shape).
export const deleteInviteRecipientSchema = {
  params: eventIdParams,
  body: {
    type: "object",
    additionalProperties: false,
    properties: {
      id: { type: "integer" },
    },
    required: ["id"],
  },
};

// Create/update the invite email campaign that holds the compose body. Always
// stored with audienceType 'invites' (set server-side), so the body omits it.
const inviteCampaignBody = {
  type: "object",
  additionalProperties: false,
  properties: {
    subject: { type: "string" },
    content: { type: ["object", "null"] },
    contentHtml: { type: ["string", "null"] },
  },
  required: ["subject"],
};

export const getInviteCampaignSchema = {
  params: eventIdParams,
};

export const saveInviteCampaignSchema = {
  params: eventIdParams,
  body: inviteCampaignBody,
};

export const web_inviteCampaignSchema = {
  ...inviteCampaignBody,
};

// Queue the invite for all recipients in the 'imported' state. The compose body
// (subject + content) is persisted as an invite-audience email campaign first,
// then referenced here by id. Responds with { queued } once the recipients are
// claimed; the mail itself goes out from `send_invites` jobs.
export const sendInvitesSchema = {
  params: eventIdParams,
  body: {
    type: "object",
    additionalProperties: false,
    properties: {
      campaignId: { type: "string" },
    },
    required: ["campaignId"],
  },
};

// Resend either to a fixed set of recipients (manual selection) or to everyone
// in an 'undelivered' state (bounced). The invite campaign is resolved
// server-side (one per event). Queued like the initial send; responds with
// { queued, skipped } where skipped is the per-recipient cooldown's work.
export const resendInvitesSchema = {
  params: eventIdParams,
  body: {
    type: "object",
    additionalProperties: false,
    properties: {
      recipientIds: {
        type: "array",
        items: { type: "integer" },
        minItems: 1,
      },
      filter: { type: "string", enum: ["undelivered"] },
    },
  },
};

// web_ variant: the recipient-edit modal validates client-side.
export const web_upsertInviteRecipientSchema = {
  ...recipientRowSchema,
};
